import {assert} from 'Asserts';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import * as RegistryUtil from 'registry/RegistryUtil';
import APIUtil from 'util/APIUtil';
import ErrorSubclass from 'util/ErrorSubclass';

import {PrintBarcodeModalQuery} from './__generated/PrintBarcodeModalQuery';

export class NotPrintableError extends ErrorSubclass {}

export interface PrintableExtras {
  id: string;
  barcodeOrName: string;
  schemaId: string;
  schemaRegistryId: string;
}

export type PrintableBioentity = ExcludeInaccessible<PrintBarcodeModalQuery['bioentities'][number]>;

export type PrintableStorable = ExcludeInaccessible<PrintBarcodeModalQuery['storables'][number]>;

export type LabelPrintable = (PrintableBioentity | PrintableStorable) & PrintableExtras;

export function isPrintableContainer(
  item: LabelPrintable
): item is ExtractByTypename<PrintableStorable, 'ContainerNode'> & PrintableExtras {
  return item.__typename === 'ContainerNode';
}

export function storableLabelPrintable(storable: PrintableStorable): LabelPrintable {
  return {
    ...storable,
    barcodeOrName: storable.barcode?.value ?? storable.name,
    schemaId: storable.storableSchema.id,
    schemaRegistryId: storable.storableSchema.registry.id,
  };
}

export function bioentityLabelPrintable(bioentity: PrintableBioentity): LabelPrintable {
  const primaryRegistryId = RegistryUtil.getPrimaryRegistryIdentifierGraphQL(bioentity);

  if (!primaryRegistryId) {
    throw new NotPrintableError('Bioentities to be printed should be registered');
  }
  if (!bioentity.tagSchema) {
    throw new NotPrintableError('Registered bioentities should have a schema');
  }

  return {
    ...bioentity,
    barcodeOrName: primaryRegistryId,
    schemaId: bioentity.tagSchema.id,
    schemaRegistryId: bioentity.tagSchema.registry.id,
  };
}

// Keep in sync with benchling/label_printing/label_templates.py:label_printable_type
export function labelPrintableType(itemId: string): string {
  if (APIUtil.isStorable(itemId)) {
    return 'STORABLE';
  } else if (APIUtil.isBiologicalEntity(itemId)) {
    return 'BIOENTITY';
  }
  assert(false, 'Invalid Printable Item Type');
  return null as never;
}
